/**
 * ReelRecorder · iter v29 r020
 *
 * Reels 01-02 (Foto perfette / Stanze arredate): crossfade prima/dopo dentro
 * IPhone17ProFrame — markup interno del recorder.
 *
 * Reels 03-08: usano DIRETTAMENTE i componenti Hero* della landing
 * (HeroSocialMockup, HeroVetrinaMockup, HeroAgendaMockup,
 * HeroMessaggiMockup, HeroAssistantMockup, HeroSopralluogoMockup).
 *
 * Le animazioni dei componenti landing sono identiche a quelle di prontoimmo.it
 * perché viene istanziato lo STESSO componente React. `useRecActivationKey`
 * rimonta il componente quando Playwright imposta data-rec="1" così le
 * animazioni partono in sincrono con il reel.
 *
 * HeroSopralluogoMockup contiene già un iPhone interno: viene scalato e
 * mostrato senza wrapper IPhone17ProFrame esterno (useFrame: false).
 */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import IPhone17ProFrame from "@/components/IPhone17ProFrame";

import HeroSopralluogoMockup from "@/pages/landing/HeroSopralluogoMockup";
import HeroAgendaMockup from "@/pages/landing/HeroAgendaMockup";
import HeroMessaggiMockup from "@/pages/landing/HeroMessaggiMockup";
import HeroAssistantMockup from "@/pages/landing/HeroAssistantMockup";
import HeroSocialMockup from "@/pages/landing/HeroSocialMockup";
import HeroVetrinaMockup from "@/pages/landing/HeroVetrinaMockup";

/* ─────────────────────────────────────────────────────────────
 * Hook: forza il rimount dei componenti landing quando Playwright
 * attiva data-rec=1. Necessario perché le Hero* hanno timer interni
 * (setTimeout / setInterval) che devono partire all'avvio del reel.
 * ───────────────────────────────────────────────────────────── */
function useRecActivationKey() {
  const [key, setKey] = useState(0);
  useEffect(() => {
    const root = document.querySelector('[data-testid="reel-recorder"]');
    if (!root) return;
    if (root.getAttribute("data-rec") === "1") setKey((k) => k + 1);
    const obs = new MutationObserver(() => {
      if (root.getAttribute("data-rec") === "1") setKey((k) => k + 1);
    });
    obs.observe(root, { attributes: true, attributeFilter: ["data-rec"] });
    return () => obs.disconnect();
  }, []);
  return key;
}

/* ─────────────────────────────────────────────────────────────
 * Reel 01 · Foto perfette · crossfade bagno-before / bagno-after
 * ───────────────────────────────────────────────────────────── */
const PhoneHeader = ({ appName, accent = "Pronto" }) => (
  <div style={{
    padding: "44px 22px 12px",
    display: "flex", justifyContent: "space-between", alignItems: "center",
    background: "rgba(255,255,255,0.85)",
    backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
    borderBottom: "1px solid rgba(226,222,210,0.5)",
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 600, color: "#1C1C1A", letterSpacing: "-0.01em" }}>
      <span style={{ color: "#9A968B", fontSize: 22, fontWeight: 300 }}>‹</span> {appName}
    </div>
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.24em", color: "#7C8B6E" }}>{accent}</div>
  </div>
);
const PhoneFooter = ({ text }) => (
  <div style={{
    padding: "14px 22px 18px", background: "#FFFFFF",
    borderTop: "1px solid rgba(226,222,210,0.5)",
    fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
    color: "#7C8B6E", fontWeight: 700,
  }}>{text}</div>
);

function FotoPerfetteAnim() {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#FFFFFF" }}>
      <PhoneHeader appName="Migliora foto"/>
      <div style={{ flexGrow: 1, position: "relative", overflow: "hidden", background: "#F0EDE8" }}>
        <img className="rr-mock-before" src="/landing/bagno-before.jpg" alt=""
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 1 }}/>
        <img className="rr-mock-after" src="/landing/bagno-after.jpg" alt=""
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0 }}/>
        <div className="rr-mock-badge-before" style={{ position: "absolute", top: 18, left: 18, padding: "8px 18px", borderRadius: 999, background: "rgba(28,28,26,0.78)", color: "#FFFFFF", fontSize: 12, fontWeight: 700, letterSpacing: "0.18em" }}>ORIGINALE</div>
        <div className="rr-mock-badge-after" style={{ position: "absolute", top: 18, left: 18, padding: "8px 18px", borderRadius: 999, background: "rgba(124,139,110,0.92)", color: "#FFFFFF", fontSize: 12, fontWeight: 700, letterSpacing: "0.18em", opacity: 0 }}>MIGLIORATA</div>
      </div>
      <PhoneFooter text="tocca per migliorare"/>
    </div>
  );
}

function StanzeArredateAnim() {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#FFFFFF" }}>
      <PhoneHeader appName="Arreda stanza vuota"/>
      <div style={{ flexGrow: 1, position: "relative", overflow: "hidden", background: "#F0EDE8" }}>
        <img className="rr-mock-before" src="/landing/vuoto-before.jpg" alt=""
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "30% center", opacity: 1 }}/>
        <img className="rr-mock-after" src="/landing/vuoto-after.jpg" alt=""
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "30% center", opacity: 0 }}/>
        <div className="rr-mock-badge-before" style={{ position: "absolute", top: 18, left: 18, padding: "8px 18px", borderRadius: 999, background: "rgba(28,28,26,0.78)", color: "#FFFFFF", fontSize: 12, fontWeight: 700, letterSpacing: "0.18em" }}>VUOTA</div>
        <div className="rr-mock-badge-after" style={{ position: "absolute", top: 18, left: 18, padding: "8px 18px", borderRadius: 999, background: "rgba(124,139,110,0.92)", color: "#FFFFFF", fontSize: 12, fontWeight: 700, letterSpacing: "0.18em", opacity: 0 }}>ARREDATA</div>
      </div>
      <PhoneFooter text="scegli lo stile"/>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * LandingMockupInPhone — wrapper per Hero landing dentro IPhone17ProFrame.
 * Scala il componente nativo (220/440/560 px) per fittare lo schermo
 * interno dell'iPhone (~510 px effettivi). key cambia su data-rec="1"
 * → animazioni interne (setTimeout/setInterval) ripartono in sync.
 * ───────────────────────────────────────────────────────────── */
function LandingMockupInPhone({ Component, nativeWidth = 440, padTop = 80 }) {
  const key = useRecActivationKey();
  const targetW = 500;
  const scale = targetW / nativeWidth;
  return (
    <div style={{
      width: "100%", height: "100%",
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      overflow: "hidden", background: "#FAFAF7",
      paddingTop: padTop,
    }}>
      <div style={{
        transform: `scale(${scale})`,
        transformOrigin: "top center",
        width: nativeWidth,
      }}>
        <Component key={key} reelMode />
      </div>
    </div>
  );
}

/* Sopralluogo: ha già il SUO iPhone (220px). Lo scaliamo a piena cornice
   senza wrapper IPhone17ProFrame esterno (useFrame: false). */
function SopralluogoLandingScaled() {
  const key = useRecActivationKey();
  return (
    <div style={{
      width: "100%", height: "100%",
      display: "flex", alignItems: "center", justifyContent: "center",
      overflow: "visible",
    }}>
      <div style={{ transform: "scale(2.55)", transformOrigin: "center center" }}>
        <HeroSopralluogoMockup key={key} />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * VetrinaReelAnim — Reel 05 · Annuncio + Vetrina personale
 *
 * iter v29 r071 — User M1432: «mostrami la vetrina ESATTAMENTE come la
 * nostra, che si scrolli dall'alto in basso». Riscritto:
 *   FASE 1 (~2.7s) — "Pronto sta scrivendo" — titolo + descrizione che
 *     appaiono parola per parola (caret blinking).
 *   FASE 2 (~5.3s) — Replica fedele della VetrinaPubblica (PublicListing
 *     `/listing/{public_id}`) che SCORRE da sola dall'alto al basso:
 *     hero con foto + brand → titolo + prezzo + chip → descrizione →
 *     galleria 3 foto → scheda tecnica → mappa → contatta agente.
 *     Auto-scroll lineare con translateY su container alto 1800px.
 *
 * Tutte le foto sono dalle nostre `/landing/*-after.jpg` (stessa serie
 * della landing pubblica), niente immagini stock.
 * ───────────────────────────────────────────────────────────── */
const VETRINA_TYPING_TEXT =
  "Splendido trilocale ristrutturato nel cuore di Brera, " +
  "a pochi passi dalla metro e dalle vetrine più eleganti di Milano. " +
  "Tre ambienti pensati con cura, parquet originale recuperato.";

const VETRINA_SCHEDULE = [
  // iter v29 r077 — Reel "annuncio" è scoped a 26s. La fase mockup occupa
  // dal 36% all'83% = 12,2s. Schedule writing 5s + scrolling 7s = 12s,
  // con caret blinking finale e pausa scroll a fondo per leggere il CTA.
  { phase: "writing", duration: 5000 },
  { phase: "scrolling", duration: 7000 },
];

function VetrinaWritingPhase({ progress }) {
  // iter v29 r077 — Velocità testo molto rallentata: progress * 0.65 invece
  // di 0.85. Il testo si scrive entro il 65% della fase (≈3,25s su 5s)
  // e poi resta visibile per ~1,75s con il caret che lampeggia. Tempo di
  // lettura confortevole anche su mobile.
  const visibleChars = Math.floor(VETRINA_TYPING_TEXT.length * Math.min(1, progress * 0.65));
  const shownText = VETRINA_TYPING_TEXT.slice(0, visibleChars);
  // iter v29 r077 — Header onesto: NON è una pubblicazione automatica.
  // L'annuncio viene auto-compilato e l'agente lo copia/incolla sui portali
  // (Idealista ha API più aperte, Immobiliare.it richiede ancora paste manuale).
  const PORTALS = ["Idealista", "Immobiliare.it"];
  // Le pillole appaiono in sequenza: prima entrambe vuote, poi si
  // "spuntano" come "Pronto · testo pronto da copiare".
  // 0-50% → 0 portali; 50-80% → 1 portale; 80-100% → 2 portali.
  const portalsActive = progress > 0.80 ? 2 : progress > 0.50 ? 1 : 0;
  return (
    <div style={{
      width: "100%", height: "100%",
      display: "flex", flexDirection: "column",
      background: "#FAF8F4",
      fontFamily: "'Montserrat', sans-serif",
    }}>
      <div style={{
        padding: "52px 24px 14px",
        display: "flex", alignItems: "center", gap: 10,
        borderBottom: "1px solid rgba(0,0,0,0.06)",
      }}>
        <span style={{
          width: 8, height: 8, borderRadius: "50%",
          background: "#7C8B6E",
          boxShadow: "0 0 0 4px rgba(124,139,110,0.18)",
          animation: "vp-pulse 1.2s infinite",
        }}/>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.22em", color: "#7C8B6E", textTransform: "uppercase" }}>
          Pronto sta scrivendo
        </span>
        <span style={{ marginLeft: "auto", fontSize: 9, fontWeight: 600, letterSpacing: "0.18em", color: "#9A968B", textTransform: "uppercase" }}>
          STEP 2/4
        </span>
      </div>
      <div style={{ margin: "18px 24px 0", height: 220, borderRadius: 8, overflow: "hidden", position: "relative" }}>
        <img src="/landing/sala-original-after.jpg" alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}/>
        <div style={{ position: "absolute", bottom: 8, left: 8, padding: "3px 10px", background: "rgba(255,255,255,0.95)", color: "#1C1C1A", fontSize: 9, fontWeight: 700, letterSpacing: "0.18em", borderRadius: 3 }}>
          IN VENDITA
        </div>
      </div>
      <div style={{ padding: "18px 24px 4px", fontSize: 24, fontWeight: 600, lineHeight: 1.1, letterSpacing: "-0.01em", color: "#1C1C1A", fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
        <em>Trilocale ristrutturato</em>
      </div>
      <div style={{ padding: "0 24px 14px", fontSize: 13, fontWeight: 500, color: "#7C8B6E", letterSpacing: "0.01em" }}>
        Brera · Milano · 95 mq
      </div>
      <div style={{ padding: "0 24px 18px", fontSize: 14, lineHeight: 1.55, color: "#3F3D38", letterSpacing: "0.005em", minHeight: 110 }}>
        {shownText}
        <span style={{
          display: "inline-block", width: 2, height: 16,
          background: "#1C1C1A", marginLeft: 2, verticalAlign: "text-bottom",
          animation: "vp-caret 0.85s steps(2) infinite",
        }}/>
      </div>

      {/* Striscia portali — il testo è pronto da copiare sui portali.
          Non promette pubblicazione automatica (Immobiliare.it richiede
          paste manuale): parla onesto di "AUTOCOMPILAZIONE". */}
      <div style={{
        marginTop: "auto",
        padding: "16px 20px 22px",
        background: "linear-gradient(180deg, rgba(241,244,237,0) 0%, rgba(241,244,237,1) 18%)",
      }}>
        <div style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.22em",
          color: "#7C8B6E", textTransform: "uppercase", marginBottom: 4,
        }}>
          Pronto per i portali
        </div>
        <div style={{
          fontSize: 10, fontWeight: 500, color: "#9A968B",
          marginBottom: 10, letterSpacing: "0.01em",
        }}>
          Le tue parole diventano un annuncio
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {PORTALS.map((p, i) => {
            const active = i < portalsActive;
            return (
              <div key={p} style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "7px 13px",
                borderRadius: 999,
                background: active ? "#1C1C1A" : "#FFFFFF",
                border: active ? "1px solid #1C1C1A" : "1px solid #E5E7E0",
                color: active ? "#FAF8F4" : "#9A968B",
                fontSize: 11, fontWeight: 700,
                letterSpacing: "0.04em",
                transition: "all 420ms ease-out",
              }}>
                <span style={{
                  width: 13, height: 13, borderRadius: "50%",
                  background: active ? "#A8B89A" : "#E5E7E0",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontSize: 9, color: "#1C1C1A", fontWeight: 900,
                }}>{active ? "✓" : ""}</span>
                {p}
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes vp-pulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.3); opacity: 0.6; } }
        @keyframes vp-caret { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }
      `}</style>
    </div>
  );
}

/* VetrinaScrollPhase — Replica fedele della vetrina pubblica Pronto che
   scorre da sola dall'alto al basso durante la fase demo del reel. */
function VetrinaScrollPhase({ progress }) {
  // Container intero della vetrina ~1820px. Viewport iPhone visibile ~620px.
  // Scrolliamo da 0 a -(1820 - 620) = -1200px progressivamente.
  const SCROLL_RANGE = 1200;
  const offsetY = -Math.min(SCROLL_RANGE, progress * SCROLL_RANGE);

  return (
    <div style={{
      width: "100%", height: "100%",
      background: "#FFFFFF",
      overflow: "hidden", position: "relative",
      fontFamily: "'Montserrat', sans-serif",
    }}>
      <div style={{
        transform: `translateY(${offsetY}px)`,
        transition: "transform 80ms linear",
        willChange: "transform",
      }}>
        {/* Hero brand bar */}
        <div style={{
          padding: "52px 16px 12px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          background: "#FFFFFF",
          borderBottom: "1px solid rgba(0,0,0,0.04)",
        }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: "0.22em",
              color: "#1C1C1A", textTransform: "uppercase",
            }}>
              Studio Bianchi
            </div>
            <div style={{
              fontSize: 10, fontWeight: 700, letterSpacing: "0.15em",
              color: "#B57C5E", textTransform: "uppercase",
            }}>
              · il tuo brand
            </div>
          </div>
          {/* Pulsante "Condividi" che lampeggia, comunica "vetrina condivisibile" */}
          <div style={{
            display: "flex", alignItems: "center", gap: 5,
            padding: "5px 11px",
            borderRadius: 999,
            background: "#7C8B6E",
            color: "#FAF8F4",
            fontSize: 9, fontWeight: 700, letterSpacing: "0.18em",
            textTransform: "uppercase",
            animation: "vp-share 2s ease-in-out infinite",
          }}>
            <span style={{ fontSize: 11 }}>↗</span>
            Condividi
          </div>
        </div>

        {/* Hero photo — sala-original-after: living luminoso, foto di copertina premium (656 KB, si carica veloce) */}
        <div style={{ position: "relative", aspectRatio: "16/10", overflow: "hidden" }}>
          <img src="/landing/sala-original-after.jpg" alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }}/>
        </div>

        {/* Status band */}
        <div style={{ padding: "14px 16px 4px", background: "#FFFFFF" }}>
          <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.22em", color: "#7C8B6E", textTransform: "uppercase" }}>
            IN VENDITA
          </span>
        </div>

        {/* Title + price */}
        <div style={{ padding: "4px 16px 8px", background: "#FFFFFF" }}>
          <div style={{
            fontSize: 18, fontWeight: 700, lineHeight: 1.15,
            letterSpacing: "0.005em",
            color: "#1C1C1A", textTransform: "uppercase",
            marginBottom: 4,
          }}>
            Trilocale ristrutturato · Brera, Milano
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 6 }}>
            <div style={{ fontSize: 11, color: "#6B6A65", display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 11 }}>📍</span>
              <span>Via Solferino 12 · Milano</span>
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#1C1C1A" }}>€ 685.000</div>
          </div>
        </div>

        {/* Feature chips */}
        <div style={{ padding: "12px 16px 12px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
          {[
            { ic: "🏠", val: "3 locali" },
            { ic: "🛁", val: "1 bagno" },
            { ic: "🪜", val: "Piano 3" },
            { ic: "⚡", val: "Classe B" },
          ].map((c, i) => (
            <div key={i} style={{
              padding: "8px 6px",
              border: "1px solid #E5E7E0",
              borderRadius: 6,
              fontSize: 9.5, fontWeight: 600, color: "#1C1C1A",
              textAlign: "center", letterSpacing: "0.01em",
            }}>
              {c.val}
            </div>
          ))}
        </div>

        {/* Descrizione */}
        <div style={{ padding: "16px 16px 12px", background: "#FFFFFF", borderTop: "1px solid #F1EFE8" }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.24em", color: "#7C8B6E", textTransform: "uppercase", marginBottom: 10 }}>
            Descrizione
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.6, color: "#3F3D38" }}>
            Splendido trilocale ristrutturato nel cuore di Brera, a pochi passi dalla metro e dalle vetrine più eleganti di Milano. Tre ambienti pensati con cura, parquet originale recuperato, esposizione luminosa che attraversa la giornata. Bagno di servizio rifinito con marmo opaco, cucina abitabile aperta sul soggiorno.
          </div>
        </div>

        {/* Galleria */}
        <div style={{ padding: "20px 16px 12px", background: "#FFFFFF", borderTop: "1px solid #F1EFE8" }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.24em", color: "#7C8B6E", textTransform: "uppercase", marginBottom: 10 }}>
            Galleria · 4 foto
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            {[
              "/landing/bagno-after.jpg",
              "/landing/staging-furnished.jpg",
              "/landing/restyling-dopo.jpg",
              "/landing/rinnova-after.jpg",
            ].map((src, i) => (
              <div key={i} style={{ aspectRatio: "1/1", borderRadius: 4, overflow: "hidden" }}>
                <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }}/>
              </div>
            ))}
          </div>
        </div>

        {/* Scheda tecnica */}
        <div style={{ padding: "20px 16px 12px", background: "#FFFFFF", borderTop: "1px solid #F1EFE8" }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.24em", color: "#7C8B6E", textTransform: "uppercase", marginBottom: 6 }}>
            Scheda tecnica
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#1C1C1A", marginBottom: 14 }}>
            Caratteristiche
          </div>
          {[
            { ic: "🏢", k: "Tipologia", v: "Appartamento" },
            { ic: "📄", k: "Contratto", v: "Vendita" },
            { ic: "🛏️", k: "Locali", v: "3" },
            { ic: "🛁", k: "Bagni", v: "1" },
            { ic: "📅", k: "Anno", v: "1965" },
            { ic: "⚡", k: "Classe energetica", v: "B" },
          ].map((row, i) => (
            <div key={i} style={{
              padding: "10px 0", display: "flex", alignItems: "center",
              borderBottom: "1px solid #F1EFE8",
            }}>
              <div style={{ width: 28, fontSize: 14 }}>{row.ic}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#1C1C1A" }}>{row.k}</div>
                <div style={{ fontSize: 10, color: "#9A968B", marginTop: 2 }}>{row.v}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Mappa */}
        <div style={{ padding: "20px 16px 16px", background: "#FFFFFF", borderTop: "1px solid #F1EFE8" }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.24em", color: "#7C8B6E", textTransform: "uppercase", marginBottom: 4 }}>
            Milano
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#1C1C1A", marginBottom: 10, textTransform: "uppercase" }}>
            Via Solferino 12
          </div>
          <div style={{
            height: 160, borderRadius: 6,
            background: "#EDEAE2",
            backgroundImage:
              "linear-gradient(to right, #DAD8CE 1px, transparent 1px), " +
              "linear-gradient(to bottom, #DAD8CE 1px, transparent 1px)",
            backgroundSize: "20px 20px",
            position: "relative",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <span style={{ fontSize: 28 }}>📍</span>
          </div>
        </div>

        {/* Contatta agente */}
        <div style={{ padding: "24px 16px 20px", background: "#F1F4ED", borderTop: "1px solid #F1EFE8" }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.24em", color: "#7C8B6E", textTransform: "uppercase", marginBottom: 6 }}>
            Contatta
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#1C1C1A", marginBottom: 16 }}>
            Parliamo di questa casa
          </div>
          <div style={{
            background: "#FFFFFF",
            border: "1px solid #E5E7E0",
            borderRadius: 8,
            padding: "20px 14px",
            display: "flex", flexDirection: "column", alignItems: "center",
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: 999,
              background: "#FAF8F4", border: "1.5px solid #7C8B6E",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 14, fontWeight: 700, color: "#7C8B6E",
              marginBottom: 8,
            }}>SB</div>
            <div style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.20em", color: "#7C8B6E", textTransform: "uppercase", marginBottom: 4 }}>
              Studio Bianchi
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#1C1C1A", marginBottom: 14 }}>
              Sofia Bianchi
            </div>
            <div style={{ width: 30, height: 1, background: "#E5E7E0", marginBottom: 14 }}/>
            <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 7 }}>
              <div style={{ padding: "9px 0", background: "#25D366", color: "white", borderRadius: 999, textAlign: "center", fontSize: 11, fontWeight: 700 }}>
                WhatsApp
              </div>
              <div style={{ padding: "9px 0", background: "#FFFFFF", color: "#1C1C1A", border: "1px solid #E5E7E0", borderRadius: 999, textAlign: "center", fontSize: 11, fontWeight: 700 }}>
                Chiama
              </div>
              <div style={{ padding: "9px 0", background: "#7C8B6E", color: "white", borderRadius: 999, textAlign: "center", fontSize: 11, fontWeight: 700 }}>
                Prenota visita
              </div>
            </div>
          </div>
          <div style={{ textAlign: "center", marginTop: 18, fontSize: 9, fontWeight: 600, letterSpacing: "0.22em", color: "#9A968B", textTransform: "uppercase" }}>
            Generata con <span style={{ color: "#1C1C1A", fontWeight: 800 }}>PRONTO</span>
          </div>
        </div>
      </div>

      {/* Indicatore scroll laterale (estetico) */}
      <div style={{
        position: "absolute", top: "50%", right: 4,
        transform: "translateY(-50%)",
        width: 3, height: 100, background: "rgba(0,0,0,0.06)",
        borderRadius: 999, overflow: "hidden",
      }}>
        <div style={{
          width: "100%",
          height: `${Math.min(100, progress * 100)}%`,
          background: "#7C8B6E",
          transition: "height 80ms linear",
        }}/>
      </div>
      <style>{`
        @keyframes vp-share {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(124,139,110,0.6); }
          50% { transform: scale(1.05); box-shadow: 0 0 0 6px rgba(124,139,110,0); }
        }
      `}</style>
    </div>
  );
}

function VetrinaReelAnim() {
  const key = useRecActivationKey();
  const [step, setStep] = useState(0);
  const [phaseProgress, setPhaseProgress] = useState(0);

  useEffect(() => {
    setStep(0);
    setPhaseProgress(0);
    let cancelled = false;
    let stepTimer;
    let progressTimer;

    const runStep = (i) => {
      if (cancelled) return;
      setStep(i);
      setPhaseProgress(0);
      const cur = VETRINA_SCHEDULE[i];
      const startedAt = Date.now();
      clearInterval(progressTimer);
      progressTimer = setInterval(() => {
        const p = Math.min(1, (Date.now() - startedAt) / cur.duration);
        setPhaseProgress(p);
      }, 40);
      stepTimer = setTimeout(() => {
        runStep((i + 1) % VETRINA_SCHEDULE.length);
      }, cur.duration);
    };
    runStep(0);

    return () => {
      cancelled = true;
      clearTimeout(stepTimer);
      clearInterval(progressTimer);
    };
  }, [key]);

  const cur = VETRINA_SCHEDULE[step];
  if (cur.phase === "writing") {
    return <VetrinaWritingPhase progress={phaseProgress} />;
  }
  return <VetrinaScrollPhase progress={phaseProgress} />;
}

/* ─────────────────────────────────────────────────────────────
 * SocialReelAnim — Contenuti social (Reel 04)
 *
 * Cicla tra 8 contenuti social grandi mostrando l'ampiezza dei
 * formati prodotti da Pronto. Ogni tile riempie 80% dello schermo
 * iPhone, con header tile name + chip social destinations sotto.
 *
 * 8 tile (era 4): aggiunti Story IG, Reel verticale, Volantino A5,
 * Carosello cucina. "Restyling" ora mostra il vero prima/dopo del
 * virtual staging (stanza vuota → arredata) con badge VUOTA/ARREDATA,
 * coerente con il prima/dopo del bagno.
 * ───────────────────────────────────────────────────────────── */
const SOCIAL_TILES = [
  {
    id: "carosello",
    label: "Carosello Instagram",
    sub: "5 slide · trilocale Brera",
    color: "#7C8B6E",
    type: "grid",
  },
  {
    id: "prima_dopo",
    label: "Prima → Dopo",
    sub: "Restyling foto bagno",
    color: "#B57C5E",
    type: "prima-dopo",
  },
  {
    id: "story",
    label: "Story Instagram",
    sub: "9:16 · annuncio del giorno",
    color: "#E1306C",
    type: "story",
  },
  {
    id: "open_house",
    label: "Open House",
    sub: "Sabato 11:00 · invito",
    color: "#1C1C1A",
    type: "open-house",
  },
  {
    id: "virtual_staging",
    label: "Virtual staging",
    sub: "Stanza vuota → arredata",
    color: "#5A6A4D",
    type: "virtual-staging",
  },
  {
    id: "reel_video",
    label: "Reel video",
    sub: "15 secondi · cinematic",
    color: "#1C1C1A",
    type: "reel-video",
  },
  {
    id: "carosello_cucina",
    label: "Post & carosello cucina",
    sub: "Prima / dopo cucina",
    color: "#B57C5E",
    type: "carousel-kitchen",
  },
  {
    id: "volantino",
    label: "Volantino A5",
    sub: "Stampa o digitale · col tuo QR",
    color: "#1C1C1A",
    type: "flyer",
  },
];

function SocialReelAnim() {
  const key = useRecActivationKey();
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % SOCIAL_TILES.length);
    }, 1300);
    return () => clearInterval(t);
  }, [key]);

  const t = SOCIAL_TILES[idx];

  return (
    <div style={{
      width: "100%", height: "100%",
      display: "flex", flexDirection: "column",
      background: "#FAFAF7",
      fontFamily: "'Montserrat', sans-serif",
    }}>
      {/* Header */}
      <div style={{
        padding: "60px 24px 18px",
        background: "rgba(255,255,255,0.95)",
        backdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
      }}>
        <div style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.28em",
          color: "#7C8B6E", textTransform: "uppercase", marginBottom: 4,
        }}>04 · CONTENUTI SOCIAL</div>
        <div style={{
          fontSize: 22, fontWeight: 600, color: "#1C1C1A",
          letterSpacing: "-0.012em", marginBottom: 2,
        }}>{t.label}</div>
        <div style={{ fontSize: 12, color: "#7C8B6E", fontWeight: 500 }}>{t.sub}</div>
      </div>

      {/* Contenuto principale grande */}
      <div style={{
        flex: 1,
        padding: "16px 18px 0",
        display: "flex", alignItems: "stretch", justifyContent: "center",
        background: "#FAFAF7",
      }}>
        <div style={{
          width: "100%",
          flex: 1,
          minHeight: 0,
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 20px 60px -20px rgba(0,0,0,0.25), inset 0 0 0 1px rgba(0,0,0,0.06)",
          background: "#FFFFFF",
          position: "relative",
        }}>
          {/* Tipologie diverse di contenuto */}
          {t.type === "grid" && (
            <div style={{
              width: "100%", height: "100%",
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: 2, background: "#1C1C1A",
            }}>
              <div style={{ background: `url('/landing/living-after.jpg') center/cover` }}/>
              <div style={{ background: `url('/landing/camera-after.jpg') center/cover` }}/>
              <div style={{ background: `url('/landing/cucina-after.jpg') center/cover` }}/>
              <div style={{
                background: "#1C1C1A", color: "#FAF8F4",
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                padding: 20, textAlign: "center", gap: 8,
              }}>
                <div style={{ fontSize: 10, letterSpacing: "0.28em", color: "#A8B89A", fontWeight: 700 }}>+ 5 SLIDE</div>
                <div style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.1, fontFamily: "'Cormorant Garamond', serif" }}><em>Bilocale</em><br/>a Brera</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", letterSpacing: "0.08em" }}>SCORRI →</div>
              </div>
            </div>
          )}

          {t.type === "prima-dopo" && (
            <div style={{ position: "relative", width: "100%", height: "100%" }}>
              <img src="/landing/bagno-before.jpg" alt="" style={{ position: "absolute", inset: 0, width: "50%", height: "100%", objectFit: "cover" }}/>
              <img src="/landing/bagno-after.jpg" alt="" style={{ position: "absolute", top: 0, left: "50%", width: "50%", height: "100%", objectFit: "cover" }}/>
              <div style={{ position: "absolute", top: 0, left: "50%", width: 2, height: "100%", background: "#FFFFFF", transform: "translateX(-50%)" }}/>
              <div style={{ position: "absolute", top: 16, left: 16, padding: "5px 12px", background: "rgba(28,28,26,0.8)", color: "#FFFFFF", fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", borderRadius: 999 }}>PRIMA</div>
              <div style={{ position: "absolute", top: 16, right: 16, padding: "5px 12px", background: "rgba(124,139,110,0.92)", color: "#FFFFFF", fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", borderRadius: 999 }}>DOPO</div>
            </div>
          )}

          {t.type === "open-house" && (
            <div style={{
              width: "100%", height: "100%",
              background: "#1C1C1A", color: "#FAF8F4",
              padding: 36, display: "flex", flexDirection: "column",
              justifyContent: "space-between",
            }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.32em", color: "#C8A66B", marginBottom: 12 }}>OPEN HOUSE</div>
                <div style={{ fontSize: 32, fontWeight: 500, lineHeight: 1.1, fontFamily: "'Cormorant Garamond', serif", letterSpacing: "-0.012em" }}>
                  <em>Sabato</em><br/>15 giugno
                </div>
                <div style={{ fontSize: 22, fontWeight: 600, color: "#C8A66B", marginTop: 18, letterSpacing: "-0.01em" }}>11:00 — 13:00</div>
              </div>
              <div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", marginBottom: 4, fontWeight: 500 }}>Loft Isola · 95 mq</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", letterSpacing: "0.06em" }}>Via Borsieri 30 · Milano</div>
                <div style={{ marginTop: 18, padding: "10px 16px", background: "#C8A66B", color: "#1C1C1A", fontSize: 11, fontWeight: 700, letterSpacing: "0.22em", textAlign: "center", borderRadius: 999 }}>PRENOTA LA TUA VISITA</div>
              </div>
            </div>
          )}

          {t.type === "virtual-staging" && (
            <div style={{ position: "relative", width: "100%", height: "100%" }}>
              <img src="/landing/vuoto-before.jpg" alt="" style={{ position: "absolute", inset: 0, width: "50%", height: "100%", objectFit: "cover" }}/>
              <img src="/landing/vuoto-after.jpg" alt="" style={{ position: "absolute", top: 0, left: "50%", width: "50%", height: "100%", objectFit: "cover" }}/>
              <div style={{ position: "absolute", top: 0, left: "50%", width: 2, height: "100%", background: "#FFFFFF", transform: "translateX(-50%)" }}/>
              <div style={{ position: "absolute", top: 16, left: 16, padding: "5px 12px", background: "rgba(28,28,26,0.8)", color: "#FFFFFF", fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", borderRadius: 999 }}>VUOTA</div>
              <div style={{ position: "absolute", top: 16, right: 16, padding: "5px 12px", background: "rgba(124,139,110,0.92)", color: "#FFFFFF", fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", borderRadius: 999 }}>ARREDATA</div>
              <div style={{ position: "absolute", bottom: 14, left: 16, right: 16, padding: "8px 12px", background: "rgba(28,28,26,0.78)", color: "#FFFFFF", fontSize: 11, fontWeight: 600, letterSpacing: "0.04em", borderRadius: 8, textAlign: "center" }}>
                Stesso angolo · 5 secondi
              </div>
            </div>
          )}

          {t.type === "story" && (
            <div style={{
              width: "100%", height: "100%",
              position: "relative", overflow: "hidden",
              background: "#000",
            }}>
              <img src="/landing/living-after.jpg" alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}/>
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 25%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.85) 100%)" }}/>
              {/* progress bars story */}
              <div style={{ position: "absolute", top: 10, left: 12, right: 12, display: "flex", gap: 4 }}>
                {[0, 1, 2].map((i) => (
                  <div key={i} style={{ flex: 1, height: 3, background: "rgba(255,255,255,0.3)", borderRadius: 2 }}>
                    <div style={{ height: "100%", width: i === 0 ? "100%" : i === 1 ? "60%" : "0%", background: "#FFFFFF", borderRadius: 2 }}/>
                  </div>
                ))}
              </div>
              {/* handle agente */}
              <div style={{ position: "absolute", top: 22, left: 12, display: "flex", alignItems: "center", gap: 8, color: "#FFFFFF" }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#7C8B6E", border: "2px solid #FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>P</div>
                <div style={{ fontSize: 11, fontWeight: 600, textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}>casetua_milano</div>
              </div>
              {/* badge */}
              <div style={{ position: "absolute", top: "32%", left: "50%", transform: "translateX(-50%) rotate(-6deg)", padding: "8px 18px", background: "#C8A66B", color: "#1C1C1A", fontSize: 13, fontWeight: 700, letterSpacing: "0.22em", borderRadius: 999, boxShadow: "0 8px 24px -8px rgba(0,0,0,0.5)" }}>
                NUOVO IN VENDITA
              </div>
              {/* testo info */}
              <div style={{ position: "absolute", bottom: 80, left: 18, right: 18, color: "#FFFFFF" }}>
                <div style={{ fontSize: 22, fontWeight: 600, fontFamily: "'Cormorant Garamond', serif", lineHeight: 1.05, letterSpacing: "-0.01em" }}>
                  <em>Trilocale</em><br/>vista Duomo
                </div>
                <div style={{ fontSize: 12, marginTop: 6, fontWeight: 500, color: "rgba(255,255,255,0.85)" }}>95 mq · €580.000</div>
              </div>
              {/* swipe up */}
              <div style={{ position: "absolute", bottom: 18, left: 18, right: 18, padding: "10px 16px", background: "rgba(255,255,255,0.95)", color: "#1C1C1A", fontSize: 11, fontWeight: 700, letterSpacing: "0.2em", textAlign: "center", borderRadius: 999 }}>
                SCOPRI →
              </div>
            </div>
          )}

          {t.type === "reel-video" && (
            <div style={{
              width: "100%", height: "100%",
              background: "#1C1C1A",
              position: "relative", overflow: "hidden",
            }}>
              <img src="/landing/esterno-after.jpg" alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}/>
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(28,28,26,0.0) 45%, rgba(28,28,26,0.88) 100%)" }}/>
              <div style={{ position: "absolute", top: 16, left: 16, padding: "5px 12px", background: "rgba(255,255,255,0.92)", color: "#1C1C1A", fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", borderRadius: 999 }}>REEL · 0:15</div>
              <div style={{ position: "absolute", top: 16, right: 16, padding: "5px 10px", background: "#E1306C", color: "#FFFFFF", fontSize: 9, fontWeight: 700, letterSpacing: "0.18em", borderRadius: 999 }}>IG · TIKTOK</div>
              <div style={{ position: "absolute", bottom: 18, left: 18, right: 18, color: "#FFFFFF" }}>
                <div style={{ fontSize: 24, fontWeight: 600, fontFamily: "'Cormorant Garamond', serif", lineHeight: 1.05 }}><em>Una villa</em></div>
                <div style={{ fontSize: 24, fontWeight: 600, fontFamily: "'Cormorant Garamond', serif", lineHeight: 1.05, marginTop: -2 }}><em>che racconta</em></div>
                <div style={{ fontSize: 12, marginTop: 8, color: "rgba(255,255,255,0.75)", fontWeight: 500, letterSpacing: "0.03em" }}>Musica + montaggio · pronto in 1 click</div>
              </div>
              {/* Play icon */}
              <div style={{
                position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
                width: 60, height: 60, borderRadius: 999,
                background: "rgba(255,255,255,0.95)",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 12px 32px -8px rgba(0,0,0,0.4)",
              }}>
                <div style={{ width: 0, height: 0, borderTop: "11px solid transparent", borderBottom: "11px solid transparent", borderLeft: "17px solid #1C1C1A", marginLeft: 4 }}/>
              </div>
            </div>
          )}

          {t.type === "carousel-kitchen" && (
            <div style={{
              width: "100%", height: "100%",
              display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "auto 1fr",
              gap: 2, background: "#1C1C1A",
            }}>
              {/* top: prima dopo cucina */}
              <div style={{ position: "relative", gridColumn: "span 2", aspectRatio: "2 / 1" }}>
                <img src="/landing/cucina-before.jpg" alt="" style={{ position: "absolute", inset: 0, width: "50%", height: "100%", objectFit: "cover" }}/>
                <img src="/landing/cucina-after.jpg" alt="" style={{ position: "absolute", top: 0, left: "50%", width: "50%", height: "100%", objectFit: "cover" }}/>
                <div style={{ position: "absolute", top: 0, left: "50%", width: 2, height: "100%", background: "#FFFFFF", transform: "translateX(-50%)" }}/>
                <div style={{ position: "absolute", bottom: 10, left: 10, padding: "4px 10px", background: "rgba(28,28,26,0.8)", color: "#FFFFFF", fontSize: 9, fontWeight: 700, letterSpacing: "0.16em", borderRadius: 999 }}>PRIMA</div>
                <div style={{ position: "absolute", bottom: 10, right: 10, padding: "4px 10px", background: "rgba(124,139,110,0.92)", color: "#FFFFFF", fontSize: 9, fontWeight: 700, letterSpacing: "0.16em", borderRadius: 999 }}>DOPO</div>
              </div>
              {/* bottom left: post */}
              <div style={{
                background: "#FAF8F4", padding: 14, display: "flex", flexDirection: "column",
                justifyContent: "space-between",
              }}>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.2em", color: "#7C8B6E" }}>POST</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#1C1C1A", marginTop: 6, lineHeight: 1.15, fontFamily: "'Cormorant Garamond', serif" }}>
                    <em>La stessa cucina.<br/>Un secondo dopo.</em>
                  </div>
                </div>
                <div style={{ fontSize: 9, color: "#B57C5E", fontWeight: 700, letterSpacing: "0.18em" }}>↗ INSTAGRAM</div>
              </div>
              {/* bottom right: stat */}
              <div style={{
                background: "#1C1C1A", color: "#FAF8F4", padding: 14,
                display: "flex", flexDirection: "column", justifyContent: "space-between",
              }}>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.2em", color: "#C8A66B" }}>+ 5 FORMATI</div>
                  <div style={{ fontSize: 13, fontWeight: 600, marginTop: 6, lineHeight: 1.15 }}>
                    Stesso scatto.<br/>Tutti i social.
                  </div>
                </div>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", letterSpacing: "0.16em", fontWeight: 700 }}>1 CLICK</div>
              </div>
            </div>
          )}

          {t.type === "flyer" && (
            <div style={{
              width: "100%", height: "100%",
              background: "#FAF8F4",
              padding: 20,
              display: "flex", flexDirection: "column",
              boxShadow: "inset 0 0 0 10px #FAF8F4, inset 0 0 0 11px #E5E7E0",
              fontFamily: "'Montserrat', sans-serif",
            }}>
              {/* header agenzia */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 10, borderBottom: "1px solid #1C1C1A" }}>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.28em", color: "#1C1C1A" }}>CASA TUA · MILANO</div>
                <div style={{ fontSize: 8, color: "#9A968B", letterSpacing: "0.16em" }}>VOLANTINO · A5</div>
              </div>
              {/* big house photo */}
              <div style={{ flex: 1, marginTop: 10, position: "relative", borderRadius: 4, overflow: "hidden" }}>
                <img src="/landing/esterno-after.jpg" alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }}/>
                <div style={{ position: "absolute", bottom: 8, left: 8, padding: "4px 10px", background: "rgba(255,255,255,0.95)", color: "#1C1C1A", fontSize: 9, fontWeight: 700, letterSpacing: "0.18em", borderRadius: 4 }}>
                  IN VENDITA
                </div>
              </div>
              {/* titolo */}
              <div style={{ marginTop: 12, fontSize: 18, fontWeight: 600, color: "#1C1C1A", fontFamily: "'Cormorant Garamond', serif", lineHeight: 1.1 }}>
                <em>Villa indipendente</em>
              </div>
              <div style={{ fontSize: 11, color: "#6B6A65", marginTop: 2, fontWeight: 500 }}>4 locali · 180 mq · giardino</div>
              {/* footer con QR */}
              <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 12, paddingTop: 12, borderTop: "1px solid #E5E7E0" }}>
                <div style={{ width: 54, height: 54, background: "#1C1C1A", display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gridTemplateRows: "repeat(7, 1fr)", gap: 1, padding: 4, borderRadius: 4 }}>
                  {Array.from({ length: 49 }).map((_, i) => (
                    <div key={i} style={{ background: (i * 7 + 3) % 3 === 0 ? "#FAF8F4" : "transparent" }}/>
                  ))}
                </div>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.2em", color: "#7C8B6E" }}>SCANSIONA</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#1C1C1A", marginTop: 2, lineHeight: 1.1 }}>Stima OMI<br/>gratuita</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer: destinazioni social */}
      <div style={{
        padding: "18px 24px 28px",
        background: "#FFFFFF",
        borderTop: "1px solid rgba(0,0,0,0.06)",
      }}>
        <div style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.24em",
          color: "#7C8B6E", textTransform: "uppercase", marginBottom: 10,
        }}>PUBBLICA SU</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {[
            { name: "Instagram", color: "#E1306C" },
            { name: "Facebook", color: "#1877F2" },
            { name: "WhatsApp", color: "#25D366" },
            { name: "TikTok", color: "#000000" },
          ].map((s) => (
            <div key={s.name} style={{
              padding: "6px 12px",
              border: `1.5px solid ${s.color}26`,
              borderRadius: 999,
              fontSize: 11, fontWeight: 600, color: s.color,
              letterSpacing: "0.02em",
            }}>{s.name}</div>
          ))}
        </div>
      </div>

      {/* Indicator dots */}
      <div style={{
        padding: "0 0 20px",
        background: "#FFFFFF",
        display: "flex", justifyContent: "center", gap: 8,
      }}>
        {SOCIAL_TILES.map((_, i) => (
          <div key={i} style={{
            width: i === idx ? 24 : 6, height: 6, borderRadius: 999,
            background: i === idx ? "#7C8B6E" : "rgba(0,0,0,0.12)",
            transition: "all 500ms ease",
          }}/>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Config 8 reel
 *   - useFrame=true  → contenuto dentro IPhone17ProFrame del recorder
 *   - useFrame=false → contenuto già con iPhone proprio (sopralluogo)
 * ───────────────────────────────────────────────────────────── */
const REELS = {
  "foto-perfette": {
    num: "01 · OGNI GIORNO", titleA: "Foto", titleB: "perfette",
    sub: ["Scatti col cellulare.", "Pronto pensa al resto."],
    Mockup: FotoPerfetteAnim, useFrame: true,
    chips: ["Luce naturale", "Prospettive dritte", "Colori veri", "Pronta"],
    iconSvg: (<svg viewBox="0 0 100 100"><rect x="15" y="25" width="70" height="50" rx="6"/><circle cx="50" cy="50" r="14" className="accent"/><circle cx="50" cy="50" r="6"/><rect x="30" y="20" width="14" height="10" rx="2"/><circle cx="74" cy="36" r="2" fill="#B57C5E" stroke="none"/></svg>),
  },
  "stanze-arredate": {
    num: "02 · STANZA VUOTA", titleA: "Stanze", titleB: "arredate",
    sub: ["Carichi una foto.", "Cinque secondi: arredata."],
    Mockup: StanzeArredateAnim, useFrame: true,
    chips: ["Stile moderno", "Mobili veri", "5 secondi", "Pronta"],
    iconSvg: (<svg viewBox="0 0 100 100"><path d="M15 80 L15 35 L50 15 L85 35 L85 80 Z"/><path d="M30 80 L30 55 L45 55 L45 80" className="sage"/><rect x="55" y="55" width="20" height="14" className="accent"/><line x1="55" y1="48" x2="75" y2="48" className="accent"/></svg>),
  },
  "sopralluogo": {
    num: "03 · CAMMINI · PARLI", titleA: "Sopralluogo", titleB: "vocale",
    sub: ["Cammini per casa.", "Pronto annota tutto."],
    Mockup: SopralluogoLandingScaled, useFrame: false,
    chips: ["Voce", "Trascrizione", "Foto", "Scheda"],
    iconSvg: (<svg viewBox="0 0 100 100"><rect x="40" y="20" width="20" height="40" rx="10" className="accent"/><path d="M30 50 Q30 70 50 70 Q70 70 70 50"/><line x1="50" y1="70" x2="50" y2="82"/><line x1="40" y1="82" x2="60" y2="82"/></svg>),
  },
  "contenuti-social": {
    num: "04 · UN CLICK", titleA: "Contenuti", titleB: "social",
    sub: ["Un click.", "Tutto pronto da pubblicare."],
    Mockup: () => <LandingMockupInPhone Component={HeroSocialMockup} nativeWidth={500} padTop={70} />,
    useFrame: true,
    chips: ["Post", "Story", "Reel", "Volantino"],
    iconSvg: (<svg viewBox="0 0 100 100"><rect x="20" y="20" width="35" height="50" rx="2"/><line x1="26" y1="32" x2="48" y2="32" className="sage"/><line x1="26" y1="40" x2="48" y2="40" className="sage"/><rect x="55" y="35" width="25" height="35" rx="3" className="accent"/></svg>),
  },
  "annuncio": {
    num: "05 · ANNUNCIO + VETRINA", titleA: "Parli. Pronto", titleB: "scrive l'annuncio.",
    sub: ["Le tue parole, un annuncio pronto per i portali.", "Vetrina personale: 4 stili, il tuo brand."],
    Mockup: VetrinaReelAnim,
    useFrame: true,
    chips: ["Annuncio", "Idealista", "Immobiliare", "Vetrina"],
    iconSvg: (<svg viewBox="0 0 100 100"><rect x="20" y="25" width="60" height="55" rx="4"/><line x1="28" y1="38" x2="72" y2="38" className="sage"/><line x1="28" y1="48" x2="72" y2="48" className="sage"/><path d="M62 60 L72 70 L80 58" className="accent" strokeWidth="3"/></svg>),
  },
  "agenda": {
    num: "06 · MAI PIÙ DIMENTICANZE", titleA: "Agenda", titleB: "promemoria",
    sub: ["Parli o scrivi.", "Pronto crea l'evento."],
    Mockup: () => <LandingMockupInPhone Component={HeroAgendaMockup} nativeWidth={440} padTop={110}/>,
    useFrame: true,
    chips: ["Visite", "Firme", "Follow-up", "Calendar"],
    iconSvg: (<svg viewBox="0 0 100 100"><rect x="20" y="25" width="60" height="55" rx="4"/><line x1="20" y1="38" x2="80" y2="38"/><line x1="32" y1="20" x2="32" y2="32"/><line x1="68" y1="20" x2="68" y2="32"/><circle cx="50" cy="58" r="3" fill="#B57C5E" stroke="none"/></svg>),
  },
  "whatsapp": {
    num: "07 · WHATSAPP", titleA: "Messaggi", titleB: "pronti",
    sub: ["Auguri, follow-up,", "nuovi annunci, stime."],
    Mockup: () => <LandingMockupInPhone Component={HeroMessaggiMockup} nativeWidth={440} padTop={110}/>,
    useFrame: true,
    chips: ["Auguri", "Follow-up", "Nuovi annunci", "Open house"],
    iconSvg: (<svg viewBox="0 0 100 100"><path d="M20 30 Q20 25 25 25 L75 25 Q80 25 80 30 L80 60 Q80 65 75 65 L40 65 L25 78 L25 65 Q20 65 20 60 Z"/><circle cx="76" cy="36" r="6" fill="#B57C5E" stroke="none"/></svg>),
  },
  "assistente": {
    num: "08 · CASO COMPLESSO", titleA: "Assistente", titleB: "Pronto",
    sub: ["Una domanda tecnica.", "Risponde subito."],
    Mockup: () => <LandingMockupInPhone Component={HeroAssistantMockup} nativeWidth={440} padTop={110}/>,
    useFrame: true,
    chips: ["Fisco", "Mutui", "Atti", "Successioni"],
    iconSvg: (<svg viewBox="0 0 100 100"><path d="M30 35 Q30 22 50 22 Q70 22 70 35 Q70 48 50 48 L50 60" className="accent"/><circle cx="50" cy="72" r="3.5" fill="#B57C5E" stroke="none"/></svg>),
  },
};

export default function ReelRecorder() {
  const [params] = useSearchParams();
  const id = params.get("id") || "foto-perfette";
  const reel = REELS[id];

  if (!reel) {
    return (
      <div style={{ padding: 40, fontFamily: "Montserrat" }}>
        Reel id non riconosciuto: <strong>{id}</strong>
        <br /><br />Ids: {Object.keys(REELS).join(", ")}
      </div>
    );
  }

  const { num, titleA, titleB, sub, Mockup, chips, iconSvg, useFrame = true } = reel;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap');
        html, body { margin: 0; padding: 0; }
        [data-testid="cookie-banner"], .cookie-banner, .CookieConsent,
        [class*="cookie"], [id*="cookie"], [class*="Cookie"] { display: none !important; }

        [data-testid="reel-recorder"] { --rr-play: paused; --rr-dur: 18s; }
        /* iter v29 r077 — Solo il reel "annuncio" è più lungo (26s vs 18s
           degli altri 7): user feedback "i testi non si leggono". */
        [data-testid="reel-recorder"][data-rid="annuncio"] { --rr-dur: 26s; }
        [data-testid="reel-recorder"][data-rec="1"] { --rr-play: running; }

        [data-testid="reel-recorder"] .rr-cover,
        [data-testid="reel-recorder"] .rr-cover-mark,
        [data-testid="reel-recorder"] .rr-cover-l1,
        [data-testid="reel-recorder"] .rr-cover-l2,
        [data-testid="reel-recorder"] .rr-descr,
        [data-testid="reel-recorder"] .rr-descr > *,
        [data-testid="reel-recorder"] .rr-demo,
        [data-testid="reel-recorder"] .rr-x-kicker,
        [data-testid="reel-recorder"] .rr-x-title,
        [data-testid="reel-recorder"] .rr-x-phone,
        [data-testid="reel-recorder"] .rr-x-chip,
        [data-testid="reel-recorder"] .rr-mock-before,
        [data-testid="reel-recorder"] .rr-mock-after,
        [data-testid="reel-recorder"] .rr-mock-badge-before,
        [data-testid="reel-recorder"] .rr-mock-badge-after,
        [data-testid="reel-recorder"] .rr-outro,
        [data-testid="reel-recorder"] .rr-outro > *,
        [data-testid="reel-recorder"] .rr-watermark {
          animation-play-state: var(--rr-play);
        }

        /* COVER (0-15% di 18s) */
        .rr-cover { position: absolute; inset: 0; background: #1C1C1A; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 56px; opacity: 0; animation: rrCoverFadeIn var(--rr-dur) linear forwards; z-index: 1; }
        @keyframes rrCoverFadeIn { 0% { opacity: 0 } 4% { opacity: 1 } 100% { opacity: 1 } }
        .rr-cover-mark, .rr-cover-l1, .rr-cover-l2 { opacity: 0; animation-duration: var(--rr-dur); animation-timing-function: ease-out; animation-fill-mode: forwards; }
        .rr-cover-mark { animation-name: rrCoverMark; }
        .rr-cover-l1 { animation-name: rrCoverL1; }
        .rr-cover-l2 { animation-name: rrCoverL2; }
        @keyframes rrCoverMark { 0%, 2% { opacity: 0; transform: scale(0.88) } 8% { opacity: 1; transform: scale(1) } 100% { opacity: 1; transform: scale(1) } }
        @keyframes rrCoverL1 { 0%, 6% { opacity: 0; transform: translateY(16px) } 11% { opacity: 1; transform: translateY(0) } 100% { opacity: 1; transform: translateY(0) } }
        @keyframes rrCoverL2 { 0%, 9% { opacity: 0; transform: translateY(16px) } 13% { opacity: 1; transform: translateY(0) } 100% { opacity: 1; transform: translateY(0) } }

        /* DESCR (15-36%) */
        .rr-descr { position: absolute; inset: 0; background: #F1F4ED; display: flex; flex-direction: column; align-items: center; justify-content: center; opacity: 0; animation: rrDescrFadeIn var(--rr-dur) linear forwards; z-index: 2; }
        @keyframes rrDescrFadeIn { 0%, 14% { opacity: 0 } 19% { opacity: 1 } 100% { opacity: 1 } }
        .rr-descr > * { opacity: 0; animation-duration: var(--rr-dur); animation-timing-function: ease-out; animation-fill-mode: forwards; }
        .rr-d-num { animation-name: rrDNum; }
        .rr-d-icon { animation-name: rrDIcon; }
        .rr-d-div { animation-name: rrDDiv; }
        .rr-d-title { animation-name: rrDTitle; }
        .rr-d-sub { animation-name: rrDSub; }
        @keyframes rrDNum { 0%, 17% { opacity: 0 } 22% { opacity: 1 } 100% { opacity: 1 } }
        @keyframes rrDIcon { 0%, 18% { opacity: 0; transform: translateY(28px) scale(0.94) } 24% { opacity: 1; transform: translateY(0) scale(1) } 100% { opacity: 1; transform: translateY(0) scale(1) } }
        @keyframes rrDDiv { 0%, 21% { opacity: 0; transform: scaleX(0) } 26% { opacity: 1; transform: scaleX(1) } 100% { opacity: 1; transform: scaleX(1) } }
        @keyframes rrDTitle { 0%, 23% { opacity: 0; transform: translateY(16px) } 28% { opacity: 1; transform: translateY(0) } 100% { opacity: 1; transform: translateY(0) } }
        @keyframes rrDSub { 0%, 25% { opacity: 0; transform: translateY(10px) } 30% { opacity: 1; transform: translateY(0) } 100% { opacity: 1; transform: translateY(0) } }

        /* DEMO (36-83%) — TITOLI E IPHONE ALLA STESSA Y PER TUTTI */
        .rr-demo {
          position: absolute; inset: 0; background: #F1F4ED;
          display: flex; flex-direction: column;
          align-items: center; justify-content: flex-start;
          padding: 0;
          opacity: 0;
          animation: rrDemoFadeIn var(--rr-dur) linear forwards;
          z-index: 3;
        }
        @keyframes rrDemoFadeIn { 0%, 35% { opacity: 0 } 40% { opacity: 1 } 100% { opacity: 1 } }

        .rr-x-kicker {
          position: absolute; top: 130px; left: 0; right: 0;
          text-align: center;
          font-weight: 700; font-size: 22px; letter-spacing: 0.34em; color: #5A6A4D;
          opacity: 0; animation: rrXKicker var(--rr-dur) linear forwards;
        }
        .rr-x-title {
          position: absolute; top: 175px; left: 0; right: 0;
          text-align: center;
          font-weight: 600; font-size: 70px; line-height: 1.04;
          letter-spacing: -0.018em;
          white-space: nowrap;
          opacity: 0; animation: rrXTitle var(--rr-dur) linear forwards;
        }
        /* iPhone container a Y fissa per TUTTI gli 8 reel */
        .rr-x-phone {
          position: absolute; top: 320px; left: 50%;
          transform: translateX(-50%);
          width: 560px; height: 1120px;
          display: flex; align-items: center; justify-content: center;
          opacity: 0; animation: rrXPhone var(--rr-dur) linear forwards;
        }
        .rr-x-chip {
          opacity: 0; animation-duration: var(--rr-dur); animation-timing-function: ease-out;
          animation-fill-mode: forwards;
        }
        @keyframes rrXKicker { 0%, 38% { opacity: 0 } 42% { opacity: 1 } 100% { opacity: 1 } }
        @keyframes rrXTitle { 0%, 39% { opacity: 0; transform: translateY(12px) } 43% { opacity: 1; transform: translateY(0) } 100% { opacity: 1; transform: translateY(0) } }
        @keyframes rrXPhone { 0%, 40% { opacity: 0; transform: translateX(-50%) translateY(36px) scale(0.96) } 46% { opacity: 1; transform: translateX(-50%) translateY(0) scale(1) } 100% { opacity: 1; transform: translateX(-50%) translateY(0) scale(1) } }

        .rr-x-chip-0 { animation-name: rrXChip0; }
        .rr-x-chip-1 { animation-name: rrXChip1; }
        .rr-x-chip-2 { animation-name: rrXChip2; }
        .rr-x-chip-3 { animation-name: rrXChip3; }
        @keyframes rrXChip0 { 0%, 73% { opacity: 0; transform: translateY(8px) } 76% { opacity: 1; transform: translateY(0) } 100% { opacity: 1; transform: translateY(0) } }
        @keyframes rrXChip1 { 0%, 75% { opacity: 0; transform: translateY(8px) } 78% { opacity: 1; transform: translateY(0) } 100% { opacity: 1; transform: translateY(0) } }
        @keyframes rrXChip2 { 0%, 77% { opacity: 0; transform: translateY(8px) } 80% { opacity: 1; transform: translateY(0) } 100% { opacity: 1; transform: translateY(0) } }
        @keyframes rrXChip3 { 0%, 79% { opacity: 0; transform: translateY(8px) } 82% { opacity: 1; transform: translateY(0) } 100% { opacity: 1; transform: translateY(0) } }

        /* Reel 01/02 crossfade · 55-78% */
        .rr-mock-before { animation: rrMockBefore var(--rr-dur) linear forwards; }
        .rr-mock-after { animation: rrMockAfter var(--rr-dur) linear forwards; }
        @keyframes rrMockBefore { 0%, 55% { opacity: 1 } 78% { opacity: 0 } 100% { opacity: 0 } }
        @keyframes rrMockAfter { 0%, 55% { opacity: 0 } 78% { opacity: 1 } 100% { opacity: 1 } }
        .rr-mock-badge-before { animation: rrMockBadgeBefore var(--rr-dur) linear forwards; }
        .rr-mock-badge-after { animation: rrMockBadgeAfter var(--rr-dur) linear forwards; }
        @keyframes rrMockBadgeBefore { 0%, 60% { opacity: 1 } 70% { opacity: 0 } 100% { opacity: 0 } }
        @keyframes rrMockBadgeAfter { 0%, 68% { opacity: 0 } 78% { opacity: 1 } 100% { opacity: 1 } }

        /* OUTRO */
        .rr-outro { position: absolute; inset: 0; background: #1C1C1A; display: flex; flex-direction: column; align-items: center; justify-content: center; opacity: 0; animation: rrOutroFadeIn var(--rr-dur) linear forwards; z-index: 4; }
        @keyframes rrOutroFadeIn { 0%, 83% { opacity: 0 } 88% { opacity: 1 } 100% { opacity: 1 } }
        .rr-outro > * { opacity: 0; animation-duration: var(--rr-dur); animation-timing-function: ease-out; animation-fill-mode: forwards; }
        .rr-o-tag { animation-name: rrOTag; }
        .rr-o-mark { animation-name: rrOMark; }
        .rr-o-site { animation-name: rrOSite; }
        .rr-o-cta { animation-name: rrOCta; }
        @keyframes rrOTag { 0%, 85% { opacity: 0; transform: translateY(8px) } 89% { opacity: 1; transform: translateY(0) } 100% { opacity: 1; transform: translateY(0) } }
        @keyframes rrOMark { 0%, 88% { opacity: 0; transform: scale(0.94) } 92% { opacity: 1; transform: scale(1) } 100% { opacity: 1; transform: scale(1) } }
        @keyframes rrOSite { 0%, 91% { opacity: 0 } 95% { opacity: 1 } 100% { opacity: 1 } }
        @keyframes rrOCta { 0%, 94% { opacity: 0 } 98% { opacity: 1 } 100% { opacity: 1 } }

        .rr-watermark { position: absolute; bottom: 60px; right: 80px; display: flex; flex-direction: column; align-items: center; gap: 8px; z-index: 3; opacity: 0; animation: rrWM var(--rr-dur) linear forwards; }
        @keyframes rrWM { 0%, 18% { opacity: 0 } 24% { opacity: 0.7 } 81% { opacity: 0.7 } 87%, 100% { opacity: 0 } }
      `}</style>

      <div
        data-testid="reel-recorder"
        data-rid={id}
        style={{
          width: 1080, height: 1920, background: "#1C1C1A",
          fontFamily: "'Montserrat', system-ui, sans-serif", color: "#1C1C1A",
          position: "relative", overflow: "hidden", margin: 0,
        }}
      >
        {/* COVER */}
        <div className="rr-cover">
          <img className="rr-cover-mark" src="/logo/mark-symbol-128.png" alt=""
            style={{ width: 220, height: 220, filter: "brightness(0) invert(0.92)" }}/>
          <div className="rr-cover-l1" style={{ fontWeight: 500, fontSize: 100, lineHeight: 1.04, color: "#FAF8F4", textAlign: "center", letterSpacing: "-0.01em" }}>Le case</div>
          <div className="rr-cover-l2" style={{ fontWeight: 700, fontSize: 100, lineHeight: 1.04, color: "#A8B89A", textAlign: "center", letterSpacing: "-0.01em", marginTop: -24 }}>le vendi tu.</div>
        </div>

        {/* DESCR */}
        <div className="rr-descr">
          <div className="rr-d-num" style={{ position: "absolute", top: 200, left: 80, fontWeight: 700, fontSize: 26, letterSpacing: "0.32em", color: "#5A6A4D" }}>{num}</div>
          <div className="rr-d-icon" style={{ width: 280, height: 280, borderRadius: 32, background: "#FAF8F4", boxShadow: "0 28px 56px -20px rgba(28,28,26,0.20), inset 0 1px 0 rgba(255,255,255,0.6)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 70 }}>
            <div style={{ width: 140, height: 140 }}>
              {React.cloneElement(iconSvg, { style: { width: "100%", height: "100%" }, stroke: "#1C1C1A", strokeWidth: 1.6, fill: "none", strokeLinecap: "round", strokeLinejoin: "round" })}
            </div>
          </div>
          <div className="rr-d-div" style={{ width: 90, height: 3, background: "#7C8B6E", marginBottom: 48, transformOrigin: "center" }}/>
          <div className="rr-d-title" style={{ fontWeight: 600, fontSize: 110, lineHeight: 1.02, textAlign: "center", letterSpacing: "-0.018em", marginBottom: 36, maxWidth: 920 }}>
            {titleA} <span style={{ color: "#5A6A4D", fontWeight: 400 }}>{titleB}</span>
          </div>
          <div className="rr-d-sub" style={{ fontWeight: 300, fontSize: 50, lineHeight: 1.38, textAlign: "center", color: "#6B6A65", maxWidth: 900 }}>
            {sub[0]}<br/>{sub[1]}
          </div>
        </div>

        {/* DEMO · TUTTI iPhone alla stessa Y, titolo alla stessa Y */}
        <div className="rr-demo">
          <div className="rr-x-kicker">{num}</div>
          <div className="rr-x-title">
            {titleA} <span style={{ color: "#5A6A4D", fontWeight: 400 }}>{titleB}</span>
          </div>
          <div className="rr-x-phone">
            {useFrame ? (
              <IPhone17ProFrame size="full" tone="titanium">
                <div style={{ width: "100%", height: "100%", position: "relative", overflow: "hidden" }}>
                  <Mockup />
                </div>
              </IPhone17ProFrame>
            ) : (
              <Mockup />
            )}
          </div>

          <div style={{
            position: "absolute", bottom: 200, left: 0, right: 0,
            display: "flex", justifyContent: "center", gap: 12,
            padding: "0 40px", flexWrap: "nowrap",
          }}>
            {chips.map((c, i) => (
              <div key={i} className={`rr-x-chip rr-x-chip-${i}`} style={{
                padding: "14px 22px", borderRadius: 999,
                background: "#FAF8F4",
                border: "1.5px solid rgba(124,139,110,0.45)",
                fontWeight: 600, fontSize: 24, color: "#1C1C1A",
                letterSpacing: "-0.005em", whiteSpace: "nowrap",
                boxShadow: "0 6px 18px -6px rgba(28,28,26,0.10)",
              }}>{c}</div>
            ))}
          </div>
        </div>

        <div className="rr-watermark">
          <img src="/logo/mark-symbol-128.png" alt="" style={{ width: 64, height: 64 }}/>
          <span style={{ fontWeight: 500, fontSize: 24, color: "#5A6A4D", letterSpacing: "0.005em" }}>prontoimmo.it</span>
        </div>

        <div className="rr-outro">
          <div className="rr-o-tag" style={{ fontWeight: 500, fontSize: 88, lineHeight: 1.04, color: "#FAF8F4", textAlign: "center", letterSpacing: "-0.01em", marginBottom: 60 }}>
            Al resto<br/>
            <span style={{ fontWeight: 700, color: "#A8B89A" }}>ci pensa Pronto.</span>
          </div>
          <img className="rr-o-mark" src="/logo/mark-symbol-128.png" alt=""
            style={{ width: 220, height: 220, marginBottom: 32, filter: "brightness(0) invert(0.92)" }}/>
          <div className="rr-o-site" style={{ fontWeight: 500, fontSize: 48, color: "#A8B89A", letterSpacing: "0.005em", marginBottom: 44 }}>prontoimmo.it</div>
          <div className="rr-o-cta" style={{ fontWeight: 600, fontSize: 30, color: "#B57C5E", letterSpacing: "0.22em", textTransform: "uppercase" }}>— scoprilo —</div>
        </div>
      </div>
    </>
  );
}
